import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { collection, addDoc, query, where, getDocs } from 'firebase/firestore';
import { auth, storage, db } from '../firebase/config';

// Simple direct upload function - no date calculation, just use the file directly
export const uploadAudioFile = async (file) => {
  try {
    // Check authentication
    if (!auth.currentUser) {
      console.error('Authentication error: No user logged in');
      throw new Error('You must be logged in to upload files');
    }

    console.log('DIRECT UPLOAD - Using exact file name:', file.name);
    
    // Create storage reference using the exact file name
    const storageRef = ref(storage, `audio/${file.name}`);
    
    console.log('Storage reference created:', {
      fullPath: storageRef.fullPath,
      bucket: storage.app.options.storageBucket,
      name: storageRef.name
    });

    // Upload file with metadata
    const metadata = {
      contentType: 'audio/mpeg',
      customMetadata: {
        uploadedBy: auth.currentUser.email,
        uploadDate: new Date().toISOString()
      }
    };

    console.log('Starting upload with exact file name...');
    const uploadResult = await uploadBytes(storageRef, file, metadata);
    console.log('File uploaded successfully:', uploadResult);

    // Get download URL
    const downloadURL = await getDownloadURL(uploadResult.ref);
    console.log('Download URL obtained:', downloadURL);

    // Record in Firestore
    try {
      await addDoc(collection(db, 'audio_files'), {
        fileName: file.name,
        downloadURL,
        uploadedBy: auth.currentUser.email,
        uploadedAt: new Date(),
        fileSize: file.size,
        contentType: 'audio/mpeg'
      });
    } catch (firestoreError) {
      console.warn('Warning: Could not record metadata in Firestore:', firestoreError);
      // Continue even if Firestore update fails
    }

    return {
      success: true,
      fileName: file.name,
      downloadURL,
      path: `audio/${file.name}`
    };
  } catch (error) {
    console.error('Upload error details:', {
      code: error.code,
      message: error.message,
      fileName: file.name,
      stack: error.stack
    });
    throw new Error(`Failed to upload ${file.name}: ${error.message}`);
  }
};

export const uploadMultipleAudioFiles = async (files) => {
  if (!files || files.length === 0) {
    throw new Error('No files provided for upload');
  }

  console.log('DIRECT UPLOAD - Starting batch upload of', files.length, 'files');
  
  const results = [];
  const errors = [];

  for (const file of files) {
    try {
      console.log(`Processing file with direct upload: ${file.name}`);
      const result = await uploadAudioFile(file);
      results.push(result);
    } catch (error) {
      console.error(`Error uploading ${file.name}:`, error);
      errors.push({
        fileName: file.name,
        error: error.message
      });
    }
  }

  if (errors.length > 0) {
    const errorMessage = errors.map(e => `${e.fileName}: ${e.error}`).join('\n');
    throw new Error(`Failed to upload ${errors.length} file(s):\n${errorMessage}`);
  }

  return results;
};

// Get audio file URL by day of year
export const getAudioFileUrl = async (dayOfYear) => {
  try {
    // Convert day of year to date string (YYYY-MM-DD format)
    const currentYear = new Date().getFullYear();
    const date = new Date(currentYear, 0, 1);
    date.setDate(date.getDate() + dayOfYear - 1);
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const dateString = `${currentYear}-${month}-${day}`;
    
    console.log(`Looking for audio file: ${dateString}.mp3 for day ${dayOfYear}`);
    
    // First try to get the file directly from storage
    try {
      const audioRef = ref(storage, `audio/${dateString}.mp3`);
      const url = await getDownloadURL(audioRef);
      console.log(`Found audio file in storage: ${dateString}.mp3`);
      return url;
    } catch (storageError) {
      console.log(`No audio file found in storage for ${dateString}.mp3, checking Firestore`);
      
      // If not found in storage, try Firestore
      const q = query(collection(db, 'audio_files'), where('fileName', '==', `${dateString}.mp3`));
      const querySnapshot = await getDocs(q);
      
      if (!querySnapshot.empty) {
        console.log(`Found audio file in Firestore: ${dateString}.mp3`);
        return querySnapshot.docs[0].data().downloadURL;
      }
      
      console.log(`No audio file found for day ${dayOfYear} (${dateString})`);
      return null;
    }
  } catch (error) {
    console.error('Error getting audio file URL:', error);
    return null;
  }
}; 